import React, { useContext, useState, useMemo, useEffect } from 'react';
import { makeStyles, useTheme } from '@mui/styles';
import { Table, TableBody, Box, Typography, useMediaQuery, Chip, IconButton } from '@mui/material';
import { KeyboardArrowRight, KeyboardArrowDown } from '@mui/icons-material';
import { UserContext } from '../../lib/contexts/UserContext';
import MobileNutrientRow from './components/MobileNutrientRow';
import NutrientRow from './components/NutrientRow';
import NutrientTableHeader from './components/NutrientTableHeader';
import MacronutrientRow from './components/MacronutrientRow';
import MobileMacronutrientRow from './components/MobileMacronutrientRow';
import MacronutrientHeader from './components/MacronutrientHeader';
import { getWorkflowToken } from '../../lib/helpers/helpers';
import { calculateNutrientScores, calculateNutrientChange } from './utils/nutrientCalculations';
import useNutrients from '../../lib/hooks/useNutrients';

const useStyles = makeStyles((theme) => ({
  title: {
    fontSize: '18.72px',
    fontWeight: 700,
    lineHeight: '116.7%',
    textTransform: 'capitalize',
    color: theme.palette.black87,
    marginBottom: '16px',
  },
  mobileLayout: {
    display: 'flex',
    flexDirection: 'column',
    width: '313px',
    gap: '8px',
    position: 'relative',
    overflow: 'hidden',
    borderRadius: '8px',
  },
  patientChartCard: {
    ...theme.globalClasses.patientChartCard,
    ...theme.globalClasses.adminTableWrapper,
    maxWidth: '790px', // this may need to be adjusted
    width: '100%',
    tableLayout: 'fixed',
    borderRadius: '8px',
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      maxWidth: '313px',
      width: '313px',
    },
  },
  unitBold: {
    fontWeight: 600,
    margin: 0,
  },
  unitRegular: {
    fontWeight: 400,
  },
  mobileTitleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  mobileTitle: {
    fontSize: '18.72px',
    fontWeight: 700,
    lineHeight: '116.7%',
    textTransform: 'capitalize',
    color: theme.palette.black87,
    margin: 0,
  },
}));

// TBD put colors in light.js
// warning/dark color #E65100
// warning/_states/selected #EF6C00 8%
// success/main color #1B5E20
// success/_states/selected #2E7D32 8%
export const getNutrientChange = (type, current, goal, unit, classes) => {
  const { value } = calculateNutrientChange(current, goal, unit);

  if (!value && value !== 0) {
    return null;
  }

  if (value === 0 || type?.trim() === 'fats' || type?.trim() === 'macronutrients') {
    return (
      <p className={classes.unitBold}>
        {value} <span className={classes.unitRegular}>{unit}</span>
      </p>
    );
  }

  return (
    <Chip
      label={
        <>
          <span className={classes.unitBold}>{value}</span>
          <span className={classes.unitRegular}>&nbsp;{unit}</span>
        </>
      }
      sx={{
        backgroundColor: value < 0 ? '#EF6C0014' : '#2E7D3214',
        color: value < 0 ? '#E65100' : '#1B5E20',
        height: 24,
        '& .MuiChip-label': {
          padding: '0 8px',
        },
      }}
    />
  );
};

/**
 * NutrientsBox displays different types of nutrition data based on type
 * - macronutrients
 * - nutrients
 * - fats
 * - minerals
 * - vitamins
 *
 * @param {string} type - Type of nutrition data to display
 * @param {string} workflowToken - Optional workflow token
 * @param {Array} nutritionData - Optional nutrition data
 * @param {boolean} isMobile - Optional flag to force mobile view
 */
const NutrientsBox = ({
  type = 'nutrients',
  workflowToken = null,
  nutritionData = null,
  isMobile: forceMobile = false, // Add this prop with default false
}) => {
  const classes = useStyles();
  const theme = useTheme();
  // Use forceMobile prop to override responsive behavior if provided
  const isMobile = forceMobile || useMediaQuery(theme.breakpoints.down('md'));
  const [currentView, setCurrentView] = useState('current');
  const [expanded, setExpanded] = useState(false);
  const {
    state: { userObject },
  } = useContext(UserContext);
  const resolvedWorkflowToken = useMemo(() => {
    if (workflowToken) return workflowToken;
    return getWorkflowToken(userObject?.workflowShareUrl);
  }, [workflowToken, userObject?.workflowShareUrl]);

  // Always call the hook, but we'll decide which data to use later
  const { data: fetchedData, isLoading: dataArrayLoading } = useNutrients(resolvedWorkflowToken, type);

  // Determine which data source to use
  const dataArray = useMemo(() => {
    if (nutritionData) return nutritionData;
    return fetchedData;
  }, [nutritionData, fetchedData]);

  const hasIdeal = useMemo(() => {
    if (!dataArray || !Array.isArray(dataArray)) return false;
    return dataArray.some((item) => 'goal' in item);
  }, [dataArray]);

  // Define the implementation function
  const formatMacronutrientDataImpl = (data) => {
    if (!data || !Array.isArray(data)) return null;

    // Find nutrients by name instead of index with improved error handling
    const findNutrient = (name) => {
      const item = data.find(
        (item) =>
          item && item.name && typeof item.name === 'string' && item.name.toLowerCase().includes(name.toLowerCase()),
      );
      return item || { current: 0, goal: 0, unit: 'g' };
    };

    const calories = findNutrient('calorie');
    const carbs = findNutrient('carbohydrate');
    const fat = findNutrient('fat');
    const protein = findNutrient('protein');

    // Get values from found nutrients
    const currentCarbs = Number(carbs.current) || 0;
    const currentFat = Number(fat.current) || 0;
    const currentProtein = Number(protein.current) || 0;

    // Calculate total calories from macronutrients
    const totalCalories = currentCarbs * 4 + currentFat * 9 + currentProtein * 4;

    // Helper function to calculate macronutrient data
    const createMacronutrientData = (nutrient, name, multiplier) => {
      const current = Number(nutrient.current) || 0;
      const goal = Number(nutrient.goal) || 0;
      const currentCalories = current * multiplier;
      const goalCalories = goal * multiplier;

      return {
        name: nutrient.name || name,
        current: current.toString(),
        currentPercentage: totalCalories > 0 ? Math.round((currentCalories / totalCalories) * 100) : null,
        goal: goal.toString(),
        goalPercentage: totalCalories > 0 ? Math.round((goalCalories / totalCalories) * 100) : null,
        unit: nutrient.unit || 'g',
        allCurrentPercentages: {
          carbohydrates: totalCalories > 0 ? Math.round(((currentCarbs * 4) / totalCalories) * 100) : 0,
          totalFat: totalCalories > 0 ? Math.round(((currentFat * 9) / totalCalories) * 100) : 0,
          protein: totalCalories > 0 ? Math.round(((currentProtein * 4) / totalCalories) * 100) : 0,
        },
      };
    };

    // Create macronutrient data with proper fallbacks
    const macroData = {
      calories: {
        name: calories.name || 'Estimated Calorie Intake',
        current: calories.current || '0',
        goal: calories.current || '0', // this is the same as current
        unit: calories.unit || 'kcal',
      },
      macronutrients: [
        createMacronutrientData(carbs, 'Carbohydrates', 4),
        createMacronutrientData(fat, 'Total Fat', 9),
        createMacronutrientData(protein, 'Protein', 4),
      ],
    };

    return {
      ...macroData,
      calories: {
        ...macroData.calories,
        calculatedChange: null,
      },
      macronutrients: macroData.macronutrients.map((nutrient) => ({
        ...nutrient,
        calculatedChange: getNutrientChange(
          'macronutrients',
          Number(nutrient.current),
          Number(nutrient.goal),
          nutrient.unit,
          classes,
        ),
      })),
    };
  };

  // Memoize the result of the formatting function
  const formatMacronutrientData = useMemo(() => {
    return formatMacronutrientDataImpl(dataArray);
  }, [dataArray, classes]);

  // Memoize the formatted data object that's used multiple times in rendering
  const formattedMacroData = useMemo(() => {
    return formatMacronutrientData;
  }, [formatMacronutrientData]);

  // Memoize the nutrient data calculation function
  const getNutrientData = useMemo(() => {
    return ({ type, item, index }) => {
      const scores = calculateNutrientScores(item);
      const calculatedChange = getNutrientChange(type, Number(item.current), Number(item.goal), item.unit, classes);
      const longDescription = item?.description;

      return {
        ...scores,
        calculatedChange,
        longDescription,
      };
    };
  }, [classes]);

  if (isMobile) {
    return (
      <Box className={classes.mobileLayout}>
        {type !== 'macronutrients' && (
          <Box className={classes.mobileTitleContainer}>
            <IconButton
              onClick={() => setExpanded(!expanded)}
              size="medium"
              sx={{ padding: '4px' }}
            >
              {expanded ? (
                <KeyboardArrowDown sx={{ height: '32px', width: '32px' }} />
              ) : (
                <KeyboardArrowRight sx={{ height: '32px', width: '32px' }} />
              )}
            </IconButton>
            <Typography className={classes.mobileTitle}>{type}</Typography>
          </Box>
        )}

        {(expanded || type === 'macronutrients') && (
          <Table>
            {type === 'macronutrients' ? (
              <MacronutrientHeader
                variant="mobile"
                onViewChange={setCurrentView}
                currentView={currentView}
                hasIdeal={hasIdeal}
              />
            ) : (
              <NutrientTableHeader
                variant="mobile"
                hasIdeal={hasIdeal}
              />
            )}
            <TableBody>
              {type === 'macronutrients' ? (
                <>
                  <MobileMacronutrientRow
                    name={formattedMacroData?.calories.name}
                    current={formattedMacroData?.calories.current}
                    goal={formattedMacroData?.calories.goal}
                    unit={formattedMacroData?.calories.unit}
                    calculatedChange={formattedMacroData?.calories.calculatedChange}
                    isCalorieRow
                    currentView={currentView}
                  />
                  {formattedMacroData?.macronutrients.map((nutrient, index, array) => (
                    <MobileMacronutrientRow
                      key={index}
                      name={nutrient.name}
                      current={nutrient.current}
                      currentPercentage={nutrient.currentPercentage}
                      goal={nutrient.goal}
                      goalPercentage={nutrient.goalPercentage}
                      unit={nutrient.unit}
                      calculatedChange={nutrient.calculatedChange}
                      allCurrentPercentages={nutrient.allCurrentPercentages}
                      currentView={currentView}
                      isLastRow={index === array.length - 1}
                    />
                  ))}
                </>
              ) : (
                dataArray.map((item, index) => (
                  <MobileNutrientRow
                    key={index}
                    item={item}
                    {...getNutrientData({ type, item, index })}
                  />
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Box>
    );
  }

  return (
    <Table className={classes.patientChartCard}>
      {type === 'macronutrients' ? (
        <MacronutrientHeader
          variant="desktop"
          type={type}
          hasIdeal={hasIdeal}
        />
      ) : (
        <NutrientTableHeader
          variant="desktop"
          type={type}
          onExpandChange={setExpanded}
          hasIdeal={hasIdeal}
        />
      )}
      {(expanded || type === 'macronutrients') && (
        <TableBody>
          {type === 'macronutrients' ? (
            <>
              <MacronutrientRow
                calories={formattedMacroData?.calories}
                isCalorieRow
              />
              <MacronutrientRow macronutrients={formattedMacroData?.macronutrients} />
            </>
          ) : (
            dataArray.map((item, index) => (
              <NutrientRow
                type={type}
                key={index}
                item={item}
                {...getNutrientData({ type, item, index })}
              />
            ))
          )}
        </TableBody>
      )}
    </Table>
  );
};

export default NutrientsBox;
